import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertCircle, Plus } from 'lucide-react';
import {
  mapProductError,
  useAuthSession,
  useStoreCategories,
  useTranslation,
} from '@dukanti/shared';
import type { ProductWithImages, TranslationKey } from '@dukanti/shared';
import { supabase } from '@/lib/supabase';
import { useCurrentStore } from '@/hooks/use-current-store';
import { useProducts } from '@/hooks/use-products';
import { useStore } from '@/features/store-settings/use-store';
import { Button } from '@/components/ui/button';
import { BarcodeSearchCompact } from '@/features/stock/components';
import { ProductCard } from '@/features/products/components/product-card';
import { ProductsEmptyState } from '@/features/products/components/products-empty-state';
import { ProductsListSkeleton } from '@/features/products/components/products-list-skeleton';
import { DeleteProductDialog } from '@/features/products/components/delete-product-dialog';
import { PublishProductDialog } from '@/features/products/components/publish-product-dialog';
import { UnpublishProductDialog } from '@/features/products/components/unpublish-product-dialog';
import {
  matchesProductCatalogFilter,
  toProductCatalogRow,
} from '@/features/products/components/product-catalog-model';
import type {
  ProductCatalogFilter,
  ProductCatalogRow,
} from '@/features/products/components/product-catalog-model';
import { ProductsCatalogTable } from '@/features/products/components/products-catalog-table';
import { cn } from '@/lib/utils';

/**
 * F-003 products list route. Owned by F4c-1.
 *
 * State model:
 *   - loading          → <ProductsListSkeleton />
 *   - error            → inline error card with a `refetch` button
 *   - empty (data=[])  → <ProductsEmptyState /> with a big CTA
 *   - populated        → 3-column product grid with a limit note
 *
 * Per design D5, clicking a product card opens the detail view. Per
 * design D4, the "Add product" CTA goes to `/products/new`.
 *
 * The kebab-menu delete action currently sets local state which will
 * be consumed by the delete-confirm dialog landing with F4c-5. Until
 * that component arrives, `productPendingDelete` stays live but is
 * not rendered, which keeps the route compilable and avoids changing
 * the delete wiring in a follow-up PR.
 */
export function ProductsListRoute() {
  const { t, dir } = useTranslation();
  const navigate = useNavigate();
  const authSession = useAuthSession(supabase);
  const { storeId } = useCurrentStore(authSession);
  const { data, isLoading, isError, error, refetch } = useProducts(storeId);
  const categories = useStoreCategories(supabase, storeId);

  // F-028: resolve storeSlug + storePublished so cards can enforce the
  // Publish button disable state and dialogs can build the public URL
  // preview. Matches dashboard-route's useStore pattern.
  const store = useStore(storeId);
  const storeSlug = store.data?.slug ?? null;
  const storePublished = store.data?.published === true;

  // Lifted state: F4c-5 will wire the delete-confirm dialog to this slot.
  // Until that dialog exists we hold the pending product here (effectively
  // a no-op), which lets the product-card kebab menu compile against a
  // real callback.
  const [productPendingDelete, setProductPendingDelete] = useState<ProductWithImages | null>(null);
  // F-028: lifted state for publish / unpublish dialogs — mirrors the
  // `productPendingDelete` pattern; the dialog is keyed on the pending
  // product's id so re-opening for a different row resets state.
  const [productPendingPublish, setProductPendingPublish] = useState<ProductWithImages | null>(
    null,
  );
  const [productPendingUnpublish, setProductPendingUnpublish] = useState<ProductWithImages | null>(
    null,
  );
  const [activeFilter, setActiveFilter] = useState<ProductCatalogFilter>('all');

  function goToNewProduct() {
    navigate('/products/new');
  }

  const showSkeleton = isLoading || (!data && !isError);
  const isEmpty = !!data && data.length === 0;
  const isPopulated = !!data && data.length > 0;
  const catalogRows = useMemo<ProductCatalogRow[]>(
    () => (data ?? []).map(toProductCatalogRow),
    [data],
  );
  const filteredRows = useMemo(
    () => catalogRows.filter((row) => matchesProductCatalogFilter(row, activeFilter)),
    [activeFilter, catalogRows],
  );
  const summary = useMemo(
    () => ({
      totalInWindow: catalogRows.length,
      published: catalogRows.filter((row) => row.publicationState === 'published').length,
      draft: catalogRows.filter((row) => row.publicationState === 'draft').length,
      unavailable: catalogRows.filter((row) => row.available <= 0).length,
    }),
    [catalogRows],
  );
  const filters: ReadonlyArray<{
    readonly value: ProductCatalogFilter;
    readonly labelKey: TranslationKey;
    readonly count: number;
    readonly testId: string;
  }> = [
    {
      value: 'all',
      labelKey: 'admin.products.filters.all',
      count: summary.totalInWindow,
      testId: 'products-list-filter-all',
    },
    {
      value: 'published',
      labelKey: 'admin.products.filters.published',
      count: summary.published,
      testId: 'products-list-filter-published',
    },
    {
      value: 'draft',
      labelKey: 'admin.products.filters.draft',
      count: summary.draft,
      testId: 'products-list-filter-draft',
    },
    {
      value: 'unavailable',
      labelKey: 'admin.products.filters.unavailable',
      count: summary.unavailable,
      testId: 'products-list-filter-unavailable',
    },
  ];
  const summaryItems: ReadonlyArray<{
    readonly key: string;
    readonly labelKey: TranslationKey;
    readonly value: number;
  }> = [
    {
      key: 'all',
      labelKey: 'admin.products.summary.all',
      value: summary.totalInWindow,
    },
    {
      key: 'published',
      labelKey: 'admin.products.summary.published',
      value: summary.published,
    },
    {
      key: 'draft',
      labelKey: 'admin.products.summary.draft',
      value: summary.draft,
    },
    {
      key: 'unavailable',
      labelKey: 'admin.products.summary.unavailable',
      value: summary.unavailable,
    },
  ];

  return (
    <main
      dir={dir}
      data-testid="products-list-route"
      className="mx-auto w-full max-w-7xl px-4 py-6 sm:px-6 lg:px-8"
    >
      {/* Page header */}
      <div
        className="mb-6 flex flex-col justify-between gap-4 sm:flex-row sm:items-start"
        data-testid="products-list-header"
      >
        <div>
          <h1
            className="font-display text-foreground text-start text-[28px] font-bold leading-tight tracking-tight"
            data-testid="products-list-title"
          >
            {t('admin.products.title')}
          </h1>
          <p
            className="text-muted-foreground mt-1 max-w-2xl text-start text-sm"
            data-testid="products-list-subtitle"
          >
            {t('admin.products.subtitle')}
          </p>
        </div>
        {/* Primary add-product CTA is hidden when the empty state is shown
            (which has its own centered CTA) to avoid two CTAs on one screen. */}
        <div
          className="flex flex-col gap-2 sm:items-end"
          data-testid="products-barcode-receiving-entry"
        >
          {!isEmpty ? (
            <Button type="button" onClick={goToNewProduct} data-testid="products-list-add-cta">
              <Plus className="h-4 w-4" strokeWidth={2} />
              {t('admin.products.addNew')}
            </Button>
          ) : null}
          <BarcodeSearchCompact
            surface="products"
            onSearch={(rawBarcode) =>
              navigate(`/stock/barcode?barcode=${encodeURIComponent(rawBarcode)}`, {
                state: { prefillBarcode: rawBarcode },
              })
            }
            onScan={() =>
              navigate('/stock/barcode', {
                state: { openCamera: true },
              })
            }
          />
        </div>
      </div>

      {/* Error state — takes over the content area with a retry affordance */}
      {isError ? (
        <div
          role="alert"
          data-testid="products-list-error"
          className="border-border bg-card shadow-admin-card mx-auto max-w-lg rounded-lg border p-8 text-center"
        >
          <div className="text-destructive mb-4 flex justify-center">
            <AlertCircle className="h-10 w-10" strokeWidth={1.5} />
          </div>
          <h2 className="font-display text-foreground mb-2 text-xl">{t('common.error')}</h2>
          <p className="text-muted-foreground mb-5 text-sm">
            {t(mapProductError(error) as TranslationKey)}
          </p>
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              void refetch();
            }}
            data-testid="products-list-error-retry"
          >
            {t('common.retry')}
          </Button>
        </div>
      ) : null}

      {/* Loading skeleton — only when the query is in flight and not errored. */}
      {!isError && showSkeleton ? <ProductsListSkeleton /> : null}

      {/* Empty state */}
      {!isError && isEmpty ? <ProductsEmptyState onAddProduct={goToNewProduct} /> : null}

      {/* Populated grid */}
      {!isError && isPopulated ? (
        <>
          <section
            className="mb-4 grid grid-cols-2 gap-3 lg:grid-cols-4"
            data-testid="products-list-summary"
          >
            {summaryItems.map((item) => (
              <div
                key={item.key}
                className="border-border bg-card shadow-admin-card rounded-lg border px-4 py-3"
              >
                <p className="text-muted-foreground text-start text-xs font-medium">
                  {t(item.labelKey)}
                </p>
                <p className="font-display text-foreground mt-1 text-start text-2xl font-semibold">
                  {item.value}
                </p>
              </div>
            ))}
          </section>

          <div
            className="mb-4 flex flex-wrap items-center gap-2"
            aria-label={t('admin.products.filters.label')}
          >
            {filters.map((filter) => (
              <button
                key={filter.value}
                type="button"
                onClick={() => setActiveFilter(filter.value)}
                data-testid={filter.testId}
                aria-pressed={activeFilter === filter.value}
                className={cn(
                  'rounded-full border px-3 py-1.5 text-sm font-medium transition-colors',
                  activeFilter === filter.value
                    ? 'border-admin-lime bg-admin-lime text-primary-foreground'
                    : 'border-border bg-card text-muted-foreground hover:bg-admin-surface-muted hover:text-foreground',
                )}
              >
                {t(filter.labelKey)} <span dir="ltr">({filter.count})</span>
              </button>
            ))}
          </div>

          {filteredRows.length === 0 ? (
            <div
              data-testid="products-list-no-results"
              className="border-border bg-card shadow-admin-card rounded-lg border px-6 py-10 text-center"
            >
              <h2 className="font-display text-foreground text-lg font-semibold">
                {t('admin.products.noResults.title')}
              </h2>
              <p className="text-muted-foreground mt-1 text-sm">
                {t('admin.products.noResults.subtitle')}
              </p>
            </div>
          ) : (
            <>
              <div className="hidden md:block" data-testid="products-list-table">
                <ProductsCatalogTable
                  rows={filteredRows}
                  storePublished={storePublished}
                  onRequestDelete={setProductPendingDelete}
                  onRequestPublish={setProductPendingPublish}
                  onRequestUnpublish={setProductPendingUnpublish}
                />
              </div>
              <div data-testid="products-list-mobile" className="grid grid-cols-1 gap-6 md:hidden">
                <div data-testid="products-list-grid" className="grid grid-cols-1 gap-6">
                  {filteredRows.map((row) => (
                    <ProductCard
                      key={row.product.id}
                      product={row.product}
                      onRequestDelete={setProductPendingDelete}
                      onRequestPublish={setProductPendingPublish}
                      onRequestUnpublish={setProductPendingUnpublish}
                      storePublished={storePublished}
                    />
                  ))}
                </div>
              </div>
            </>
          )}
          <p
            className="text-muted-foreground mt-4 text-center text-sm"
            data-testid="products-list-limit-note"
          >
            {t('admin.products.limitNote')}
          </p>
        </>
      ) : null}

      {storeId ? (
        <DeleteProductDialog
          product={productPendingDelete}
          storeId={storeId}
          open={productPendingDelete !== null}
          onOpenChange={(open) => {
            if (!open) {
              setProductPendingDelete(null);
            }
          }}
          onDeleted={() => {
            setProductPendingDelete(null);
            void refetch();
          }}
        />
      ) : null}
      {/* F-028: publish / unpublish dialogs. Mounted only when the
          store slug is known so the URL preview is non-null. The `key`
          prop forces a fresh component instance when the pending
          product changes, so dialog state does not leak across rows. */}
      {storeSlug && productPendingPublish ? (
        <PublishProductDialog
          key={`publish-${productPendingPublish.id}`}
          productId={productPendingPublish.id}
          storeSlug={storeSlug}
          open={productPendingPublish !== null}
          onOpenChange={(open) => {
            if (!open) {
              setProductPendingPublish(null);
            }
          }}
          inventory={productPendingPublish.inventory}
          hasCategories={categories.categories.length > 0}
        />
      ) : null}
      {storeSlug && productPendingUnpublish ? (
        <UnpublishProductDialog
          key={`unpublish-${productPendingUnpublish.id}`}
          productId={productPendingUnpublish.id}
          storeSlug={storeSlug}
          open={productPendingUnpublish !== null}
          onOpenChange={(open) => {
            if (!open) {
              setProductPendingUnpublish(null);
            }
          }}
        />
      ) : null}
    </main>
  );
}
